import clsx from "clsx";

export type MonthHeadGroup = {
  key: string;
  label: string;
  span: number;
  startIdx: number;
};

/** Monat mit der größten Überlappung zum sichtbaren Track – ohne horizontales Verschieben. */
export function dominantVisibleMonthLabel(
  months: MonthHeadGroup[],
  dayW: number,
  scrollLeft: number,
  trackVisibleWidth: number,
): string {
  const first = months[0];
  if (!first) return "";
  const viewStart = scrollLeft;
  const viewEnd = scrollLeft + Math.max(dayW, trackVisibleWidth);
  let best = first;
  let bestOverlap = Number.NEGATIVE_INFINITY;
  for (const month of months) {
    const start = month.startIdx * dayW;
    const end = start + month.span * dayW;
    const overlap = Math.min(end, viewEnd) - Math.max(start, viewStart);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = month;
    }
  }
  return best.label;
}

/** Horizontale Mitte des Monatslabels: klebt in der Viewport-Mitte, bis der nächste Monat schiebt. */
export function stickyMonthLabelCenter(
  monthStart: number,
  monthWidth: number,
  scrollCenter: number,
): number {
  const monthEnd = monthStart + monthWidth;
  const natural = monthStart + monthWidth / 2;
  if (scrollCenter < monthStart || scrollCenter > monthEnd) {
    return natural;
  }
  return scrollCenter;
}

export function isMonthGroupCurrent(key: string, today: Date): boolean {
  const [year, month] = key.split("-").map(Number);
  return today.getFullYear() === year && today.getMonth() === month;
}

export interface StickyMonthLabelsProps {
  months: MonthHeadGroup[];
  dayW: number;
  scrollLeft: number;
  trackVisibleWidth: number;
  today: Date;
  /** Label-Position im sichtbaren Kopf, nicht im transformierten Track – verhindert Wackeln. */
  viewportRelative?: boolean;
}

export function StickyMonthLabels({
  months,
  dayW,
  scrollLeft,
  trackVisibleWidth,
  today,
  viewportRelative = false,
}: StickyMonthLabelsProps) {
  const scrollCenter = scrollLeft + trackVisibleWidth / 2;
  const viewMin = scrollLeft - dayW;
  const viewMax = scrollLeft + trackVisibleWidth + dayW;

  return (
    <div className="mp-head-months-sticky" aria-hidden>
      {months.map((m) => {
        const monthStart = m.startIdx * dayW;
        const monthWidth = m.span * dayW;
        const center = stickyMonthLabelCenter(monthStart, monthWidth, scrollCenter);
        if (center < viewMin || center > viewMax) return null;

        return (
          <div
            key={m.key}
            className={clsx(
              "mp-head-month-sticky-label",
              isMonthGroupCurrent(m.key, today) && "mp-head-month-sticky-label--current",
            )}
            style={{ left: viewportRelative ? center - scrollLeft : center }}
          >
            {m.label}
          </div>
        );
      })}
    </div>
  );
}
